/* eslint-disable @typescript-eslint/naming-convention */
import { SubscriptionType } from 'services/api/entities/user/subscriptionType';
import type { Effects, FormulaField } from 'services/store/remixer/types';
import type {
  ReleaseField,
  ReleaseStatus,
} from 'services/store/distribution/types';
import type { BundleType } from 'services/store/ai/types';
import type { Nullable } from '../types';
import { EnergyType } from '../entities';

export type QueryParameters = Readonly<{ [key: string]: unknown }>;

type ErrorData = {
  error: string;
  error_description: string;
  payload?: {
    service?: string;
  };
};

export enum ErrorType {
  COPYRIGHT_VIOLATION = 'USER_SONG_COPYRIGHT_INFRINGEMENT',
  DOWNLOAD_LIMIT_REACHED = 'DOWNLOAD_LIMIT_REACHED',
}

export enum ReleaseErrorType {
  CATALOG_SONGS_CANNOT_BE_RELEASED = 'CATALOG_SONGS_CANNOT_BE_RELEASED',
  SONG_TOO_SHORT = 'SONG_TOO_SHORT',
  ONLY_SONG_OWNER_CAN_RELEASE = 'ONLY_SONG_OWNER_CAN_RELEASE',
  LIMIT_EXCEEDED = 'LIMIT_EXCEEDED',
  RELEASE_ALREADY_SUBMITTED = 'RELEASE_ALREADY_SUBMITTED',
  SONG_ALREADY_IN_RELEASE = 'SONG_ALREADY_IN_RELEASE',
}

export type ErrorTypes = ErrorType | ReleaseErrorType;

export type ExceptionCodeErrorData = ErrorData & {
  payload?: {
    exception_code?: ErrorTypes;
  };
};

export type DownloadLimitErrorData = ExceptionCodeErrorData & {
  payload?: ExceptionCodeErrorData['payload'] & {
    days_to_limit_reset?: number;
  };
};

export type ReleaseValidationErrorData = ErrorData & {
  payload?: Partial<Record<ReleaseField, string>>;
};

export type ErrorResponse<T = ErrorData> = {
  status: number;
  data: T;
};

export type DownloadLimitResponse = {
  downloads_available_today: number;
  is_warning: boolean;
  days_to_limit_reset: number;
};

// --------- SUBSCRIPTION ---------
export type CheckoutSessionResponse = {
  checkout_session_url: string;
};

export type DateResponse = {
  date: string;
  timezone_type: number;
  timezone: string;
};

export type SubscriptionInfoResponse = {
  id: string;
  payment_details: {
    last4: number;
  };
  period: {
    start: {
      date: string;
      timezone_type: number;
      timezone: string;
    };
    end: {
      date: string;
      timezone_type: number;
      timezone: string;
    };
  };
  price: {
    currency: string;
    unit_amount: number;
  };
  product: {
    id: string;
    name: string;
  };
  recurring: {
    interval: 'month' | 'year';
    interval_count: number;
  };
  status: string;
};

export type CountryResponse = {
  id: string;
  object: string;
  default_currency: string;
  supported_bank_account_currencies: Record<string, Array<string>>;
  supported_payment_currencies: Array<string>;
  supported_payment_methods: Array<string>;
  supported_transfer_countries: Array<string>;
  verification_fields: {
    company: {
      additional: Array<string>;
      minimum: Array<string>;
    };
    individual: {
      additional: Array<string>;
      minimum: Array<string>;
    };
  };
};

export type BillingInterval = 'day' | 'week' | 'month' | 'year';

export type PriceResponse = {
  id: string;
  currency: string;
  recurring: {
    interval: BillingInterval;
    interval_count: number;
  };
  unit_amount: number;
};

export type ProductFeatureResponse = {
  name: string;
  active?: boolean;
  comingSoon?: boolean;
};

export type ProductLicenseInfoResponse = {
  title: string;
  description: string;
};

export type DistributionPlan = {
  description: string;
  features: ProductFeatureResponse[];
};

export interface DistributionPlansResponse {
  distroPlus: DistributionPlan;
  distroBasic: DistributionPlan;
}

export type ProductInformationResponse = {
  title: string;
  subtitle: string;
  description: string;
  focused: boolean;
  free: boolean;
  price?: string;
  productType?: string;
  features: ProductFeatureResponse[];
  index: number;
  licenseInfo: ProductLicenseInfoResponse;
};

export type ProductAddOnsResponse = {
  distribution_included: boolean;
};

export type ProductResponse = {
  id: string;
  is_virtual: boolean;
  name: string;
  metadata: {
    product_information: string;
  };
  prices: PriceResponse[];
  is_add_on: boolean;
  add_ons_info: ProductAddOnsResponse;
};

export type AddressResponse = {
  city: Nullable<string>;
  country: Nullable<string>;
  line1: Nullable<string>;
  line2: Nullable<string>;
  postal_code: Nullable<string>;
  state: Nullable<string>;
};

export type BillingDetailsResponse = {
  first_name: Nullable<string>;
  last_name: Nullable<string>;
  price_id: Nullable<string>;
  payment_method_id: Nullable<string>;
  coupon: Nullable<string>;
  address: AddressResponse;
};

export type CustomerResponse = {
  id: string;
  email: string;
  address: Nullable<AddressResponse>;
  invoice_settings: {
    default_payment_method: string;
  };
};

export type CustomerSessionResponse = {
  created: number;
  customer: string;
  id: string;
  livemode: boolean;
  object: string;
  return_url: string;
  url: string;
};

export type SubscriptionStatus =
  | 'active'
  | 'past_due'
  | 'unpaid'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'expired';

export type IntentStatus =
  | 'requires_payment_method'
  | 'requires_confirmation'
  | 'requires_action'
  | 'processing'
  | 'canceled'
  | 'requires_capture'
  | 'succeeded';

export type IntentResponse = {
  id: string;
  client_secret: string;
  status: IntentStatus;
};

export type InvoiceResponse = {
  id: string;
  customer: string;
  payment_intent: IntentResponse;
};

export type SubscriptionResponse = {
  id: string;
  status: SubscriptionStatus;
  current_period_end: number;
  customer: CustomerResponse;
  latest_invoice: InvoiceResponse;
  pending_setup_intent: Nullable<IntentResponse>;
  items: {
    data: [
      {
        price: {
          id: string;
        };
      },
    ];
  };
};

// --------- USER ---------
export type RegisterResponse = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  profile_image_path: string;
  subscription_type: Nullable<string>;
};

export type PublicUserResponse = {
  id: string;
  email: string;
  first_name: Nullable<string>;
  last_name: Nullable<string>;
  profile_image_path: string;
  artist_names?: { id: string; name: string }[];
};

export type PrivateUserResponse = PublicUserResponse & {
  roles: Array<string>;
  email_confirmed: boolean;
  muted: boolean;
  subscription: Nullable<SubscriptionInfoResponse>;
  subscription_type: Nullable<SubscriptionType>;
  add_ons_info: {
    distribution_included: boolean;
  };
  created_at: DateResponse;
  download_limit: Nullable<DownloadLimitResponse>;
  download_limit_stems: Nullable<DownloadLimitResponse>;
  download_limit_sample_packs: Nullable<DownloadLimitResponse>;
  ai_generation_limit: Nullable<DownloadLimitResponse>;
  studio_export_limit: Nullable<DownloadLimitResponse>;
  submit_release_limit: Nullable<DownloadLimitResponse>;
  upload_audio_limit: Nullable<DownloadLimitResponse>;
};

export type TokenResponse = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  scope: Nullable<[string]>;
  expires_in: number;
};

export type UsageLimitResponse = {
  total: number;
  used: number;
  available: number;
};

export type TopUpLimitResponse = UsageLimitResponse & {
  top_up: UsageLimitResponse | null;
};

export type UsageDataResponse = {
  download_song: TopUpLimitResponse;
  download_stems: TopUpLimitResponse;
  download_sample_pack: TopUpLimitResponse;
  generate_vega_song: UsageLimitResponse;
  export_studio_song: UsageLimitResponse;
  submit_release: UsageLimitResponse;
  upload_audio: UsageLimitResponse;
};

export type AccountLimitsResponse = {
  usage_data: UsageDataResponse;
  reset_data: {
    next_reset_date: string;
    reset_date: string;
    reset_in_days: number;
  };
};

// --------- PAGINATION ---------
export type PaginationDataResponse = {
  current_page: number;
  last_page: number;
  items_per_page: number;
  items_count: number;
  total_pages: number;
  total_items: number;
};

export interface PaginatedResponse<
  ItemType,
  PaginationData = PaginationDataResponse,
> {
  items: Array<ItemType>;
  pagination_data: PaginationData;
}

// --------- TAGS ---------
export type TagResponse = {
  name: string;
  id: number | string;
  active: boolean;
};

export type TagType =
  | 'vocals'
  | 'moods'
  | 'genres'
  | 'instruments'
  | 'keys'
  | 'energies'
  | 'eras';

export type TagsListResponse = PaginatedResponse<TagResponse>;

// --------- SONGS ---------
export type SongDownloadResponse = {
  download_data: {
    id: string;
    name: string;
    url: string;
    size: number;
    download_limit: Nullable<DownloadLimitResponse>;
  };
};

export type SongListResponse = PaginatedResponse<MainSongResponse>;

export type StudioSongListResponse = PaginatedResponse<BaseSongResponse>;

export type LibrarySongListResponse = PaginatedResponse<LibrarySongResponse>;

export type LibrarySongResponse = {
  id: string;
  song: Nullable<MainSongResponse>;
  vega_song: Nullable<BaseSongResponse>;
};

export type SongQuality = 'low' | 'medium' | 'high';

export type AudioDownloadRenderingStatusResponse = {
  status: string;
  payload: string;
};

export type SongPeaksResponse = {
  version: number;
  channels: number;
  sample_rate: number;
  samples_per_pixel: number;
  bits: number;
  length: number;
  data: number[];
};

// --------- LEGAL ---------
export type SubjectResponse = {
  id: string;
  email: string;
  first_name: Nullable<string>;
  last_name: Nullable<string>;
  verified: boolean;
};

export type LegalNoticeResponse = {
  identifier: string;
  version: Nullable<number>;
};

export type ConsentResponse = {
  subject: SubjectResponse;
  legal_notices?: Nullable<Array<LegalNoticeResponse>>;
  preferences: {
    general: boolean;
  };
};

export type Instrument =
  | 'DRUMS'
  | 'BASS'
  | 'KEYS'
  | 'GUITAR'
  | 'SYNTH'
  | 'STRINGS'
  | 'BRASS WOODWIND'
  | 'TONAL PERCUSSION'
  | 'PERCUSSION'
  | 'VOCALS'
  | 'SAMPLES'
  | 'FX';

// ---- AI PLAYGROUND -----
export type GenreResponse = {
  id: string;
  name: string;
  description: string;
  image_url: string;
  template_names: string[];
  instruments: Instrument[];
  bpm: {
    low: number;
    high: number;
  };
  suggested_instruments: Instrument[];
  micro_genres: GenreResponse[];
};

export type GenresResponse = Array<GenreResponse>;

export interface SongStructure {
  id: number;
  name: string;
  description: string;
  image_url: string;
}

export type SongStructuresResponse = Array<SongStructure>;

export interface UploadUserLoopResponse {
  id: string;
}

// ---- DISCOVERY PAGE -----

export type DiscoveryDataResponse = DiscoverySectionResponse[];

export type DiscoveryTileResponse = {
  id: string;
  title: string;
  image: string;
  description?: string;
  song_id?: string;
  meta?: Record<string, Array<string>>;
};

export type DiscoverySectionResponse = {
  id: string;
  kind: string;
  title: string;
  description: string;
  tiles?: Array<DiscoveryTileResponse>;
  cta?: string;
};

export interface FormulaExtraParamsResponse {
  entropy: number | null;
  user_audio: {
    created_at: string;
    filename: string;
    id: string;
  } | null;
}

export interface FormulaResponse {
  genre: string;
  genre_blend: string | null;
  bpm: number | null;
  key: string | null;
  instruments: Instrument[] | null;
  duration: number | null;
  energy: EnergyType | null;
  entropy: number | null;
  prompt: string | null;
  structure_id: number | null;
  extra_params: FormulaExtraParamsResponse | null;
}

export type BaseSongResponse = {
  id: string;
  title: string;
  duration: number;
  is_in_library: boolean;
  music_file_path: string;
  wav_file_path: string;
  formula: Nullable<FormulaResponse>;
};

export type SongMetadataResponse = {
  composer: string | null;
  has_vocals: boolean | null;
  vocals_content: string | null;
  language: string | null;
  vocalist: string | null;
  additional_artists: string[];
  remixers: string[];
  producers: string[];
};

export type ReleaseSongResponse = BaseSongResponse & {
  metadata?: SongMetadataResponse;
};

export type MainSongResponse = BaseSongResponse & {
  tempo_bpm: number;
  genres: Array<TagResponse>;
  secondary_genres: Array<TagResponse>;
  moods: Array<TagResponse>;
  instruments: Array<TagResponse>;
  keys: Array<TagResponse>;
  energies: Array<TagResponse>;
  eras: Array<TagResponse>;
  has_vocal: boolean;
  is_free: boolean;
  has_XML: boolean;
};
// AI GENERATOR INFO GUIDE

export type InfoGuideResponse = {
  index: number;
  title: string;
  description: string;
  secondary_description?: string;
  primary_image?: string;
  secondary_image?: string;
  alt_primary?: string;
  alt_secondary?: string;
  tip?: string;
};

// DISTRIBUTION

export type ReleaseResponse = {
  id: string;
  title: string;
  artist_name: string | null;
  status: ReleaseStatus;
  genre: {
    id: string;
    name: string;
  } | null;
  cover_art: {
    full_size_path: string;
    thumbnail_path: string;
  } | null;
  songs: {
    studio_project_songs: ReleaseSongResponse[];
    vega_songs: ReleaseSongResponse[];
    user_songs: ReleaseSongResponse[];
  };
};

export type ReleaseListResponse = PaginatedResponse<ReleaseResponse>;

export type ReleasesStatsResponse = {
  DRAFT: number;
  PENDING: number;
  RELEASED: number;
  DECLINED: number;
};

// REMIXER

export interface RemixerHistoryItemResponse {
  parentSongId: string;
  formula: FormulaField;
}

export interface RemixerProjectResponse {
  id: string;
  title: string;
  duration: number;
  music_file_path: string;
  wav_file_path: string;
  created_at: string;
  is_in_library: boolean;
  effects: Record<Effects, number>;
  history: Array<RemixerHistoryItemResponse>;
  history_pointer: number;
  history_songs: Array<{
    song_id: string;
    music_file_path: string;
    duration: number;
  }>;
  last_saved_at: number;
  song: Nullable<MainSongResponse>;
  vega_song: Nullable<BaseSongResponse>;
}

export type RemixerProjectsListResponse =
  PaginatedResponse<RemixerProjectResponse>;

export interface DeleteProjectResponse {
  success: boolean;
}

// PAYOUTS

export interface GetPayoutResponse {
  cents: number;
  currency: string;
  is_enough_for_payout: boolean;
  includes_paid_revenues: boolean;
}

export interface PostPayoutResponse {
  id: string;
  status: string;
  amount: {
    cents: 0;
    currency: string;
  };
  created_at: string;
}

// AI GENERATOR TEXT TO MUSIC PLACEHOLDER

export interface TextToMusicPlaceholderResponse {
  prompts: string[];
}

// YOUTUBE CHANNEL WHITELIST

export interface ChannelResponse {
  id: string;
  created_at: string;
  url: string;
  status: string;
}

// VEGA BUNDLES

export interface AdvancedFormulaResponse extends FormulaResponse {
  prompt: null;
}

export interface TextFormulaResponse extends FormulaResponse {
  prompt: NonNullable<FormulaResponse['prompt']>;
}

export interface VegaSongResponse {
  id: string;
  title: string;
  duration: number;
  music_file_path: string;
  xml_file_path: string;
  wav_file_path: string;
  created_at: string;
  is_in_library: boolean;
  formula: FormulaResponse;
  is_preview: boolean;
}

export interface BaseBundleResponse {
  created_at: string;
  id: string;
  type: BundleType;
  capacity: number;
  is_finished: boolean;
  is_preview: boolean;
  formula: FormulaResponse;
  vega_songs: VegaSongResponse[];
}

export interface AdvancedBundleResponse extends BaseBundleResponse {
  type: BundleType.ADVANCED;
  formula: AdvancedFormulaResponse;
}

export interface TextBundleResponse extends BaseBundleResponse {
  type: BundleType.TEXT2MUSIC;
  formula: TextFormulaResponse;
}

export type BundleResponse = AdvancedBundleResponse | TextBundleResponse;

export type CreateBundleResponse = BundleResponse & {
  vega_songs: [];
};

export type BundlesResponse = PaginatedResponse<BundleResponse>;
